import { Close as CloseIcon } from 'components/icons';
import { FC, useCallback, useEffect } from 'react';
import { Control, Path, UseFormSetValue, useFormContext, useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import { CallForActionButton } from 'components/buttons/CallForActionButton';
import PermissionButton from 'components/buttons/PermissionButton';
import ContactPickerModal from 'components/forms/form-components/select-picker/ContactPickerModal';
import SelectInput from 'components/forms/inputs/rhf-inputs/SelectInput';
import AddContactModal from 'components/modals/contact/AddContactModal';
import { useModal } from 'components/modals/useModal';
import { FORM_OVERFLOW_CUSTOM_STYLE } from 'components/modals/util';
import ContactDynamicSelectPicker from 'components/party/ContactDynamicSelectPicker';
import { useInSectionEditForm } from 'components/section-edit/SectionEdit';
import { getOptionLabelNameWithEmail, mapToContactMinDto } from 'pages/contacts/util';
import { skipToken } from '@reduxjs/toolkit/query';
import {
    useGetContactByIdQuery,
    useLazyGetMinContactsWithAddressesListWithFiltersQuery,
    useLazyGetMinContactsWithRelationsListWithFiltersQuery,
} from 'store/api/contacts';
import {
    AddressDto,
    ContactDto,
    ContactMinDtoWithAddresses,
    PartyRelationshipTypeCode,
    PartyType,
} from 'types/contacts';
import { CorrespondenceDirectionType, CorrespondenceForm, CorrespondenceType } from 'types/correspondence';
import { formatAddressSimple } from 'utils/addressFormatter';
import { t } from 'utils/getTranslations';
import { useLastNameFirst } from 'utils/nameOrder';
import { requireCurrentOrSpecifiedFields } from 'utils/validators';

type AddressField = {
    field:
        | 'homeAddress'
        | 'homeLocalAddress'
        | 'correspondenceAddress'
        | 'registrationAddress'
        | 'registrationLocalAddress'
        | 'visitingAddress'
        | 'billingAddress'
        | 'billingLocalAddress';
    labelKey: string;
};

const PERSON_ADDRESS_FIELDS: AddressField[] = [
    { field: 'homeAddress', labelKey: 'home-address' },
    { field: 'homeLocalAddress', labelKey: 'home-local-address' },
    { field: 'correspondenceAddress', labelKey: 'contacts.correspondence-address' },
];

const COMPANY_ADDRESS_FIELDS: AddressField[] = [
    { field: 'registrationAddress', labelKey: 'registration-address' },
    { field: 'registrationLocalAddress', labelKey: 'registration-address-local' },
    { field: 'visitingAddress', labelKey: 'visiting-address' },
    { field: 'correspondenceAddress', labelKey: 'contacts.correspondence-address' },
];

const COMMON_ADDRESS_FIELDS: AddressField[] = [
    { field: 'billingAddress', labelKey: 'billing-address' },
    { field: 'billingLocalAddress', labelKey: 'billing-address-local' },
];

interface AddressOption {
    address: AddressDto;
    label: string;
    type: string;
    group: string;
}

const getAddressOptionsFromContact = (
    contact: ContactMinDtoWithAddresses | ContactDto | null,
    contactType: PartyType,
    contactName: string
): AddressOption[] => {
    if (!contact) return [];

    const typeSpecificFields = contactType === PartyType.PERSON ? PERSON_ADDRESS_FIELDS : COMPANY_ADDRESS_FIELDS;
    const addressFields = [...typeSpecificFields, ...COMMON_ADDRESS_FIELDS];

    return addressFields
        .map(({ field, labelKey }) => {
            const address = contact[field];
            if (!address?.address1) return null;

            const translatedLabel = t(labelKey);
            return {
                address,
                label: `${translatedLabel}: ${formatAddressSimple(address)}`,
                type: translatedLabel,
                group: contactName,
            };
        })
        .filter((option): option is AddressOption => option !== null);
};

type Props = {
    control: Control<CorrespondenceForm>;
    setValue: UseFormSetValue<CorrespondenceForm>;
    index: number;
    onRemove?: () => void;
    validationRequired?: boolean;
};

const CorrespondenceSenderOrRecipientEntry: FC<Props> = ({
    control,
    setValue,
    index,
    onRemove,
    validationRequired = true,
}) => {
    const lastNameFirst = useLastNameFirst();
    const isInSection = useInSectionEditForm();
    const { openModal, closeModal } = useModal();
    const { trigger, getFieldState, getValues } = useFormContext<CorrespondenceForm>();
    const [fetchMinContactsWithRelations] = useLazyGetMinContactsWithRelationsListWithFiltersQuery();

    const directionTypeCode = useWatch({ control, name: 'directionTypeCode' });
    const typeCode = useWatch({ control, name: 'typeCode' });
    const partyPerson = useWatch({ control, name: `sendersOrRecipients.${index}.partyPerson` });
    const partyCompany = useWatch({ control, name: `sendersOrRecipients.${index}.partyCompany` });

    // The form value is a snapshot taken at selection time; the contact picker can edit addresses
    // afterwards. Its address mutation patches this query's cache, so subscribing here keeps the
    // address options in sync with what the picker shows. `currentData` (not `data`) so a
    // just-switched contact never shows the previous contact's addresses while loading.
    const { currentData: livePartyPerson } = useGetContactByIdQuery(partyPerson?.id.toString() ?? skipToken);
    const { currentData: livePartyCompany } = useGetContactByIdQuery(partyCompany?.id.toString() ?? skipToken);

    const isAddressRequired = typeCode?.value !== CorrespondenceType.EMAIL;

    const recipientOrSenderLabel =
        directionTypeCode === CorrespondenceDirectionType.INCOMING
            ? 'correspondence.sender-name'
            : 'correspondence.recipient-name';

    const companyLabel =
        directionTypeCode === CorrespondenceDirectionType.INCOMING
            ? 'correspondence.sender-company'
            : 'correspondence.recipient-company';

    const addressLabel =
        directionTypeCode === CorrespondenceDirectionType.INCOMING
            ? 'correspondence.sender-address'
            : 'correspondence.recipient-address';

    const partyPersonFieldName =
        `sendersOrRecipients.${index}.partyPerson` as `sendersOrRecipients.${number}.partyPerson`;
    const partyCompanyFieldName =
        `sendersOrRecipients.${index}.partyCompany` as `sendersOrRecipients.${number}.partyCompany`;
    const addressFieldName =
        `sendersOrRecipients.${index}.correspondencePartyAddress` as `sendersOrRecipients.${number}.correspondencePartyAddress`;

    const openAddNewContactModal = useCallback(
        (fieldName: Path<CorrespondenceForm>, contactType: PartyType) => {
            openModal(
                <AddContactModal
                    onContactAdd={(newContact) => {
                        const mappedNewContact = mapToContactMinDto(newContact);
                        setValue(fieldName, mappedNewContact, { shouldDirty: true });
                    }}
                    contactType={contactType}
                    onClose={closeModal}
                    isCorrespondence
                />,
                closeModal,
                FORM_OVERFLOW_CUSTOM_STYLE
            );
        },
        [openModal, closeModal, setValue]
    );

    const requiredFieldsValidator = useCallback(
        (value: unknown, formValues: CorrespondenceForm) => {
            if (!validationRequired) {
                return true;
            }
            return requireCurrentOrSpecifiedFields(
                value,
                formValues,
                [partyPersonFieldName, partyCompanyFieldName],
                t('validation.requires-at-least-one-of-fields', {
                    fields: [t(recipientOrSenderLabel), t(companyLabel)].join(', '),
                })
            );
        },
        [recipientOrSenderLabel, companyLabel, partyPersonFieldName, partyCompanyFieldName, validationRequired]
    );

    const triggerSiblingValidation = useCallback(
        (siblingField: Path<CorrespondenceForm>) => {
            const { error } = getFieldState(siblingField);
            if (error) {
                trigger(siblingField);
            }
        },
        [getFieldState, trigger]
    );

    // Auto-prefill company when person is selected and has exactly 1 employment relationship
    const maybePrefillCompany = useCallback(async () => {
        const currentPerson = getValues(partyPersonFieldName);
        const currentCompany = getValues(partyCompanyFieldName);

        if (!currentPerson || currentCompany || getFieldState(partyCompanyFieldName).isDirty) {
            return;
        }

        const { data } = await fetchMinContactsWithRelations({
            prioritizeCompaniesOf: [currentPerson.id],
            filters: `partyType==${PartyType.COMPANY}`,
            page: 0,
            pageSize: 100,
        });

        // Filter companies where person is an EMPLOYEE
        const employedByCompanies = (data?.content || []).filter(({ relations }) =>
            (relations || []).some(
                ({ partyRelationshipTypeCode, otherParty }) =>
                    partyRelationshipTypeCode === PartyRelationshipTypeCode.EMPLOYEE &&
                    otherParty.id === currentPerson.id
            )
        );

        // Only prefill if EXACTLY 1 company
        if (employedByCompanies.length === 1) {
            setValue(partyCompanyFieldName as Path<CorrespondenceForm>, employedByCompanies[0], { shouldDirty: false });
        }
    }, [
        fetchMinContactsWithRelations,
        getValues,
        setValue,
        getFieldState,
        partyPersonFieldName,
        partyCompanyFieldName,
    ]);

    // Group companies by employment relationship when a person is selected
    const groupCompanies = useCallback(
        (option: ContactMinDtoWithAddresses): string | null => {
            if (!partyPerson) return null;

            const isEmployedBy = (option.relations || []).some(
                ({ partyRelationshipTypeCode, otherParty }) =>
                    partyRelationshipTypeCode === PartyRelationshipTypeCode.EMPLOYEE && otherParty.id === partyPerson.id
            );

            return isEmployedBy ? t('field.employed-by') : t('field.other');
        },
        [partyPerson]
    );

    // Group persons by employment relationship when a company is selected
    const groupPersons = useCallback(
        (option: ContactMinDtoWithAddresses): string | null => {
            if (!partyCompany) return null;

            const isEmployee = (option.relatesTo || []).some(
                ({ partyRelationshipTypeCode, otherParty }) =>
                    partyRelationshipTypeCode === PartyRelationshipTypeCode.EMPLOYEE &&
                    otherParty.id === partyCompany.id
            );

            return isEmployee ? t('field.employees') : t('field.other');
        },
        [partyCompany]
    );

    // Re-validate address field when correspondence type changes (required only for non-Email)
    useEffect(() => {
        const { error } = getFieldState(addressFieldName);
        if (error) {
            trigger(addressFieldName);
        }
    }, [typeCode?.value, addressFieldName, getFieldState, trigger]);

    // Fall back to the form-value snapshot while the live contact is still loading.
    const personOptions = partyPerson
        ? getAddressOptionsFromContact(livePartyPerson ?? partyPerson, PartyType.PERSON, partyPerson.name)
        : [];
    const companyOptions = partyCompany
        ? getAddressOptionsFromContact(livePartyCompany ?? partyCompany, PartyType.COMPANY, partyCompany.name)
        : [];
    const addressOptions = [...personOptions, ...companyOptions];

    return (
        <div className="relative flex flex-col gap-4">
            <ContactDynamicSelectPicker
                control={control}
                name={partyPersonFieldName as 'sendersOrRecipients.0.partyPerson'}
                label={recipientOrSenderLabel}
                setValue={setValue}
                paginated={{
                    fetch: (partyCompany
                        ? useLazyGetMinContactsWithRelationsListWithFiltersQuery
                        : useLazyGetMinContactsWithAddressesListWithFiltersQuery) as typeof useLazyGetMinContactsWithAddressesListWithFiltersQuery,
                    searchField: ['name', 'partyNumber', 'mainEmail'],
                    filters: `partyType==${PartyType.PERSON}`,
                    ...(partyCompany && {
                        additionalParams: { prioritizeEmployeesOf: [partyCompany.id] },
                    }),
                }}
                {...(partyCompany && { groupByStaticFields: groupPersons })}
                isPickerModalOptionEnabled={(option) => option.partyType === PartyType.PERSON}
                getOptionLabel={(contact) => getOptionLabelNameWithEmail(contact, lastNameFirst)}
                PickerModalComponent={ContactPickerModal}
                validate={{
                    require: requiredFieldsValidator,
                }}
                onChange={() => {
                    triggerSiblingValidation(partyCompanyFieldName);
                    maybePrefillCompany();
                }}
                noOptionsContent={
                    <PermissionButton
                        permissions={[
                            'CREATE.PARTIES',
                            'CREATE.PARTIES.CLIENTS',
                            'CREATE.PARTIES.LEADS_AND_NON_CLIENTS',
                        ]}
                        variant="contained"
                        color="secondary"
                        size="extraSmall"
                        onClick={() => openAddNewContactModal(partyPersonFieldName, PartyType.PERSON)}
                    >
                        <FormattedMessage id="contacts.add-new-contact" />
                    </PermissionButton>
                }
            />
            <ContactDynamicSelectPicker
                control={control}
                name={partyCompanyFieldName as 'sendersOrRecipients.0.partyCompany'}
                label={companyLabel}
                setValue={setValue}
                paginated={{
                    fetch: (partyPerson
                        ? useLazyGetMinContactsWithRelationsListWithFiltersQuery
                        : useLazyGetMinContactsWithAddressesListWithFiltersQuery) as typeof useLazyGetMinContactsWithAddressesListWithFiltersQuery,
                    searchField: ['name', 'partyNumber'],
                    filters: `partyType==${PartyType.COMPANY}`,
                    ...(partyPerson && {
                        additionalParams: { prioritizeCompaniesOf: [partyPerson.id] },
                    }),
                }}
                {...(partyPerson && { groupByStaticFields: groupCompanies })}
                isPickerModalOptionEnabled={(option) => option.partyType === PartyType.COMPANY}
                PickerModalComponent={ContactPickerModal}
                validate={{
                    require: requiredFieldsValidator,
                }}
                onChange={() => {
                    triggerSiblingValidation(partyPersonFieldName);
                }}
                noOptionsContent={
                    <PermissionButton
                        permissions={[
                            'CREATE.PARTIES',
                            'CREATE.PARTIES.CLIENTS',
                            'CREATE.PARTIES.LEADS_AND_NON_CLIENTS',
                        ]}
                        variant="contained"
                        color="secondary"
                        size="extraSmall"
                        onClick={() => openAddNewContactModal(partyCompanyFieldName, PartyType.COMPANY)}
                    >
                        <FormattedMessage id="contacts.add-new-contact" />
                    </PermissionButton>
                }
            />
            <SelectInput
                control={control}
                setValue={setValue}
                name={addressFieldName}
                label={addressLabel}
                options={addressOptions.map((opt) => ({
                    label: opt.label,
                    value: opt.address,
                    group: opt.group,
                }))}
                getOptionLabel={(option) => option.label}
                getOptionIdentifier={(option) => option.value.id ?? option.label}
                required={validationRequired && isAddressRequired}
                clearable
            />
            {onRemove && (
                <CallForActionButton
                    shape="rounded"
                    onClick={onRemove}
                    aria-label={isInSection ? t('remove') : undefined}
                    className={
                        // A section clips what sticks out of it, so there the button sits on the entry's first label.
                        isInSection
                            ? 'text-muted-foreground hover:bg-neutral-tint hover:text-foreground absolute -top-1.5 right-0 size-7 bg-transparent'
                            : 'bg-grey-0 text-fg hover:bg-grey-200 hover:text-grey-900 absolute top-[116px] -right-12'
                    }
                >
                    <CloseIcon />
                </CallForActionButton>
            )}
        </div>
    );
};

export default CorrespondenceSenderOrRecipientEntry;
